import { createTheme } from "@mui/material";
import CaretIcon from "./components/CaretIcon";

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    h8: true;
    bodySmallWeightRegular: true;
    bodySmallWeightMedium: true;
    bodySmallWeightBold: true;
    bodyMediumWeightRegular: true;
    bodyMediumWeightMedium: true;
    bodyLargeWeightRegular: true;
    bodyLargeWeightMedium: true;
    bodyLargeWeightBold: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    containedAbb: true;
    containedWhite: true;
    text: true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#0F0F0F",
      light: "#FFC9CD",
    },
    secondary: {
      main: "#E12E2E",
      light: "#FCF3F4",
    },
    grey: {
      "100": "#EBEBEB",
      "200": "#DBDBDB",
      "300": "#BABABA",
      "400": "#9F9F9F",
      "500": "#868686",
      "600": "#696969",
      "700": "#464646",
      "800": "#333333",
      "900": "#1F1F1F",
      A100: "#0F0F0F",
    },
    background: {
      default: "#FAFAFA",
      paper: "#FFFFFF",
    },
    action: {
      hover: "#DBDBDB",
    },
    info: {
      main: "#0052FF",
      "100": "#E0F0FF",
    },
  },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          padding: 0,
          margin: 0,
          border: 0,
          boxShadow: "none",

          "&::before": {
            display: "none",
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: 0,
          border: 0,
          boxShadow: "none",
          "&.Mui-expanded": {
            minHeight: "unset",
          },
        },
        content: {
          margin: "16px 0",
          "&.Mui-expanded": {
            margin: "16px 0",
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
          border: 0,
          boxShadow: "none",
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          padding: "16px",
          background: "#EBEBEB",
          color: "#696969",
          fontFamily: "ABBvoice",
          fontSize: 14,
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: 1.2,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: "0px",
          borderRadius: "0px 0px 4px 4px",

          maxHeight: "600px",

          ".MuiMenuItem-root": {
            color: "#0F0F0F",
            fontFamily: "ABBvoice",
            fontSize: 14,
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: 1.2,

            padding: "10px 14px",
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: CaretIcon,
      },
      styleOverrides: {
        root: {
          "&.MuiInputBase-root": {
            height: 41,

            borderRadius: 4,

            display: "flex",
            alignItems: "center",
            alignSelf: "stretch",

            fontFamily: "ABBvoice",
            fontSize: 14,
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: 1.2,

            color: "#0F0F0F",

            background: "#FFF",

            ".MuiInputBase-input": {
              padding: "12px 16px !important",
              minHeight: "unset",
            },

            "&:placeholder": {
              color: "#696969 !important",
            },

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#bababa",
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#0f0f0f",
            },

            "& img": {
              marginRight: "9px !important",
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            height: 41,
            padding: "8px 16px",

            display: "flex",
            alignItems: "center",
            alignSelf: "stretch",
            gap: 8,

            color: "#0F0F0F",

            fontFamily: "ABBvoice",
            fontSize: 14,
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: 1.2,

            background: "#fff",

            ".MuiInputBase-input": {
              padding: "0",
              minHeight: "unset",
            },

            "&:placeholder": {
              color: "#696969 !important",
            },

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#bababa",
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#0f0f0f",
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontFamily: "ABBvoice",
        },
      },
      variants: [
        {
          props: { variant: "h8" },
          style: {
            fontFamily: "ABBvoice",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: 1.25,
          },
        },
        {
          props: { variant: "h4" },
          style: {
            fontFamily: "ABBvoice",
            fontSize: "24px",
            fontWeight: 500,
            lineHeight: 1.25,
          },
        },
        {
          props: { variant: "bodySmallWeightRegular" },
          style: {
            fontFamily: "ABBvoice",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: 1.2,
          },
        },
        {
          props: { variant: "bodySmallWeightMedium" },
          style: {
            fontFamily: "ABBvoice",
            fontSize: "12px",
            fontWeight: 500,
            lineHeight: 1.2,
          },
        },
        {
          props: { variant: "bodySmallWeightBold" },
          style: {
            fontFamily: "ABBvoice",
            fontSize: "12px",
            fontWeight: 700,
            lineHeight: 1.2,
          },
        },
        {
          props: { variant: "bodyMediumWeightRegular" },
          style: {
            fontFamily: "ABBvoice",
            fontSize: "14px",
            fontWeight: 400,
            lineHeight: 1.2,
          },
        },
        {
          props: { variant: "bodyMediumWeightMedium" },
          style: {
            fontFamily: "ABBvoice",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: 1.2,
          },
        },
        {
          props: { variant: "bodyLargeWeightRegular" },
          style: {
            fontFamily: "ABBvoice",
            fontSize: "18px",
            fontWeight: 400,
            lineHeight: 1.2,
          },
        },
        {
          props: { variant: "bodyLargeWeightMedium" },
          style: {
            fontFamily: "ABBvoice",
            fontSize: "16px",
            fontWeight: 500,
            lineHeight: 1.2,
          },
        },
        {
          props: { variant: "bodyLargeWeightBold" },
          style: {
            fontFamily: "ABBvoice",
            fontSize: "18px",
            fontWeight: 700,
            lineHeight: 1.2,
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          padding: "12px 16px",

          borderRadius: "4px",
          border: "1px solid #bababa",
          backgroundColor: "#fff",

          fontFamily: "ABBvoice",
          fontSize: 14,
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: 1.25,
          color: "#0F0F0F",

          textTransform: "none",

          "&:hover": {
            boxShadow: "none",
          },
        },
      },
      variants: [
        {
          props: { variant: "containedAbb" },
          style: {
            display: "flex",
            height: "40px",
            padding: "12px 24px",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,

            fontFamily: "ABBvoice",
            fontSize: 14,
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: 1.25,
            color: "#FFF",

            borderRadius: "56px",

            background: "#E12E2E",

            "&:hover": {
              backgroundColor: "#E12E2E",
            },

            "&:disabled": {
              backgroundColor: "#EBEBEB",
              color: "#696969",
            },

            border: 0,
            boxShadow: "none",
          },
        },
        {
          props: { variant: "containedWhite" },
          style: {
            display: "flex",
            height: "40px",
            padding: "12px 24px",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,

            fontFamily: "ABBvoice",
            fontSize: 14,
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: 1.25,
            color: "#000",

            borderRadius: "56px",

            background: "#FFF",

            "&:hover": {
              backgroundColor: "#FFF",
            },

            border: 0,
            boxShadow: "none",
          },
        },
        {
          props: { variant: "outlined" },
          style: {
            display: "flex",
            height: "40px",
            padding: "12px 24px",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,

            fontFamily: "ABBvoice",
            fontSize: 14,
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: 1.25,
            color: "#000",

            borderRadius: "56px",
            border: "1px solid #dbdbdb",

            background: "#FFF",

            "&:hover": {
              backgroundColor: "#FFF",
            },

            boxShadow: "none",
          },
        },
        {
          props: { variant: "text" },
          style: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,

            fontFamily: "ABBvoice",
            fontSize: 14,
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: 1.25,

            color: "#E12E2E",

            borderRadius: "56px",
            background: "transparent",

            "&:hover": {
              backgroundColor: "transparent",
            },

            border: 0,
            boxShadow: "none",
          },
        },
      ],
    },
    MuiCard: {
      variants: [
        {
          props: { variant: "outlined" },
          style: {
            borderRadius: "8px",
            border: "1px solid #EBEBEB",

            display: "flex",

            padding: "24px",
            gap: "8px",
          },
        },
      ],
    },
    MuiTable: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: "#F5F5F5",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          height: "81px",
          borderColor: "#EBEBEB",
          borderWidth: "1px",
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: "0",
          height: "28px",
          width: "48px",

          "& .MuiSwitch-switchBase": {
            padding: "4px",
          },
          "& .MuiSwitch-track": {
            height: "100%",
            width: "100%",
            borderRadius: "24px",
            background: "#fff",
            border: "1px solid #868686",
          },
          "& .Mui-checked .MuiSwitch-track": {
            background: "#0f0f0f",
            border: "1px solid #0f0f0f",
          },
          "& .MuiSwitch-thumb": {
            background: "#FFF",
            height: "20px",
            border: "1px solid #868686",
            width: "20px",
          },
          "& .Mui-checked + .MuiSwitch-track": {},
        },
      },
    },
    MuiTabs: {
      defaultProps: {
        TabIndicatorProps: { style: { backgroundColor: "#E12E2E" } },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          background: "#fff",
          textTransform: "none",

          fontFamily: "ABBvoice",
          fontSize: "14px",
          fontWeight: 500,
          lineHeight: 1.2,
        },
      },
    },
  },
});

export default theme;
